import {Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, styled} from '@mui/material'
import type {PropsWithChildren} from 'react'
import {createContext, useCallback, useContext, useReducer, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

export interface ConfirmationContextType {
  confirm: (action: () => unknown | Promise<unknown>, message: string, title?: string) => unknown;
}

export const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined)


export function ConfirmationProvider({children}: PropsWithChildren) {
  const {t} = useTranslation()
  const [state, dispatch] = useReducer(confirmationReducer, initialState)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const confirm = useCallback((action: () => unknown | Promise<unknown>, message: string, title?: string) => {
    setErrorMessage(null)
    dispatch({kind: 'SHOW', title, message, action})
  }, [])

  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return
    }
    setErrorMessage(null)
    dispatch({kind: 'CANCEL'})
  }, [isSubmitting])

  const handleConfirm = useCallback(async () => {
    if (!state.isConfirming || !state.action || isSubmitting) {
      return
    }

    try {
      setIsSubmitting(true)
      setErrorMessage(null)
      await state.action()
      dispatch({kind: 'CONFIRM'})
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : t('confirm.error')
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }, [isSubmitting, state, t])

  return <ConfirmationContext.Provider value={{confirm}}>
    {children}
    {state.isConfirming && <ConfirmationDialog
      open={state.isConfirming}
      onClose={handleClose}>
      {<DialogTitle>{state.title ?? t('confirm.defaultTitle')}</DialogTitle>}
      <DialogContent>
        <DialogContentText>{state.message}</DialogContentText>
        {errorMessage ? <Alert severity="error" sx={{mt: 2}}>{errorMessage}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          <Trans i18nKey="confirm.disagree"/>
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleConfirm}
          autoFocus
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit"/> : null}
        >
          {isSubmitting ? t('confirm.loading') : t('confirm.agree')}
        </Button>
      </DialogActions>
    </ConfirmationDialog>}
  </ConfirmationContext.Provider>
}


const ConfirmationDialog = styled(Dialog, {name: 'ConfirmationDialog'})(
  ({theme}) => ({
    '& .MuiDialogActions-root': {
      padding: theme.spacing(2)
    }
  })
)

function confirmationReducer(state: ConfirmationState, action: ConfirmationAction): ConfirmationState {
  switch (action.kind) {
  case 'SHOW':
    return {
      ...state,
      isConfirming: true,
      title: action.title,
      message: action.message,
      action: action.action
    }
  case 'CANCEL':
    return {
      isConfirming: false,
      title: undefined,
      message: undefined,
      action: undefined
    }
  case 'CONFIRM':
    if (!state.isConfirming) {
      throw new Error('Confirmation dialog is not open')
    }
    return {
      isConfirming: false,
      title: undefined,
      message: undefined,
      action: undefined
    }
  default:
    return state
  }
}


const initialState: IdleState = {
  isConfirming: false
}


interface IdleState {
  isConfirming: false;
  title?: never;
  message?: never;
  action?: never;
}

interface ConfirmingState {
  isConfirming: true;
  title?: string;
  message: string;
  action: () => unknown | Promise<unknown>;
}

type ConfirmationState = IdleState | ConfirmingState

interface DisplayAction {
  kind: 'SHOW'
  title?: string
  message: string
  action: () => unknown | Promise<unknown>
}

interface CancelAction {
  kind: 'CANCEL'
}

interface ConfirmAction {
  kind: 'CONFIRM'
}


type ConfirmationAction = DisplayAction | CancelAction | ConfirmAction

export function useConfirm() {
  const context = useContext(ConfirmationContext)
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider')
  }
  return context.confirm
}
